/* istanbul ignore file */

/* This code was copied across from the equivalent file in Core */

import {
  addTypenameToDocument,
  cloneDeep,
  Observable,
  removeClientSetsFromDocument,
  removeConnectionDirectiveFromDocument,
} from "@apollo/client/utilities";
import {
  ApolloLink,
  FetchResult,
  GraphQLRequest,
  Operation,
} from "@apollo/client/link/core";
import {
  MockedResponse,
  ResultFunction,
} from "@apollo/client/utilities/testing/mocking/mockLink";

import { equal } from "@wry/equality";
import { invariant } from "ts-invariant";
/**
 * This implementation is entirely lifted from the Apollo MockProvider
 * implementation, which I then some minor modifications to support reusing the
 * mocks (useful for storybook stories and automated tests) and additional
 * logging for easier debugging.
 *
 * I've disabled some of our ESLint rules here since this is technically
 * external code and we won't get much value out of rewriting it to comply with
 * our ESLint rules.
 */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
import { print } from "graphql";

function requestToKey(request: GraphQLRequest, addTypename: boolean): string {
  const queryString =
    request.query &&
    print(addTypename ? addTypenameToDocument(request.query) : request.query);
  const requestKey = { query: queryString };
  return JSON.stringify(requestKey);
}

/**
 * Similar to the standard Apollo MockLink, but doesn't consume a mock
 * when it is used allowing it to be used in places like Storybook.
 */
export class StaticMockLink extends ApolloLink {
  public addMockedResponse(mockedResponse: MockedResponse) {
    const normalizedMockedResponse =
      this.normalizeMockedResponse(mockedResponse);
    const key = requestToKey(
      normalizedMockedResponse.request,
      this.addTypename
    );
    let mockedResponses = this.mockedResponsesByKey[key];
    if (!mockedResponses) {
      mockedResponses = [];
      this.mockedResponsesByKey[key] = mockedResponses;
    }
    mockedResponses.push(normalizedMockedResponse);
  }

  public addTypename = true;

  constructor(
    mockedResponses: ReadonlyArray<MockedResponse>,
    addTypename = true
  ) {
    super();
    this.addTypename = addTypename;
    if (mockedResponses) {
      mockedResponses.forEach((mockedResponse) => {
        this.addMockedResponse(mockedResponse);
      });
    }
  }

  private mockedResponsesByKey: { [key: string]: MockedResponse[] } = {};

  private normalizeMockedResponse(
    mockedResponse: MockedResponse
  ): MockedResponse {
    const newMockedResponse = cloneDeep(mockedResponse);
    const queryWithoutConnection = removeConnectionDirectiveFromDocument(
      newMockedResponse.request.query
    );
    invariant(queryWithoutConnection, "query is required");
    newMockedResponse.request.query = queryWithoutConnection!;
    const query = removeClientSetsFromDocument(newMockedResponse.request.query);
    if (query) {
      newMockedResponse.request.query = query;
    }
    return newMockedResponse;
  }

  public operation?: Operation;

  public request(operation: Operation): Observable<FetchResult> | null {
    // eslint-disable-next-line no-console
    console.log(
      `[ApolloStaticMockedLink] Received new GraphQL request '${operation.operationName}'`,
      operation
    );

    this.operation = operation;
    const key = requestToKey(operation, this.addTypename);
    let responseIndex = 0;
    const response = (this.mockedResponsesByKey[key] || []).find(
      (res, index) => {
        const requestVariables = operation.variables || {};
        const mockedResponseVariables = res.request.variables || {};

        // Allow loose matching by ignore variables
        if (mockedResponseVariables.__loose__) {
          responseIndex = index;
          return true;
        }

        if (equal(requestVariables, mockedResponseVariables)) {
          responseIndex = index;
          return true;
        }
        return false;
      }
    );

    let configError: Error | null = null;

    if (!response || typeof responseIndex === "undefined") {
      configError = new Error(
        `No more mocked responses for the query: ${print(
          operation.query
        )}, variables: ${JSON.stringify(operation.variables)}`
      );
    } else {
      const { newData } = response;
      if (newData) {
        response.result = newData();
        this.mockedResponsesByKey[key].push(response);
      }

      if (!response.result && !response.error) {
        configError = new Error(
          `Mocked response should contain either result or error: ${key}`
        );
      }
    }

    if (configError) {
      // eslint-disable-next-line no-console
      console.error(
        `[ApolloStaticMockedLink] Error returning mocked response for GraphQL request '${operation.operationName}'`,
        configError
      );
    }

    if (response) {
      // eslint-disable-next-line no-console
      console.log(
        `[ApolloStaticMockedLink] Successfully returned mocked response to GraphQL request '${operation.operationName}'`,
        response
      );
    }

    return new Observable((observer) => {
      const timer = setTimeout(() => {
        if (configError) {
          try {
            // The onError function can return false to indicate that
            // configError need not be passed to observer.error. For
            // example, the default implementation of onError calls
            // observer.error(configError) and then returns false to
            // prevent this extra (harmless) observer.error call.
            if (this.onError(configError, observer) !== false) {
              throw configError;
            }
          } catch (error) {
            observer.error(error);
          }
        } else if (response) {
          if (response.error) {
            observer.error(response.error);
          } else {
            if (response.result) {
              observer.next(
                typeof response.result === "function"
                  ? (response.result as ResultFunction<FetchResult>)()
                  : response.result
              );
            }
            observer.complete();
          }
        }
      }, (response && response.delay) || 0);

      return () => {
        clearTimeout(timer);
      };
    });
  }
}

export type MockApolloLink = ApolloLink & {
  operation?: Operation;
};

// Pass in multiple mocked responses, so that you can test flows that end up
// making multiple queries to the server.
// NOTE: The last arg can optionally be an `addTypename` arg.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mockSingleLink(...mockedResponses: Array<any>): MockApolloLink {
  // To pull off the potential typename. If this isn't a boolean, we'll just
  // set it true later.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  let maybeTypename: boolean = mockedResponses[mockedResponses.length - 1];
  let mocks = mockedResponses.slice(0, mockedResponses.length - 1);

  if (typeof maybeTypename !== "boolean") {
    mocks = mockedResponses;
    maybeTypename = true;
  }

  return new StaticMockLink(mocks, maybeTypename);
}
